import { inject, Injectable, OnDestroy } from "@angular/core";
import { Data } from "@angular/router";
import { AuthService } from "@core/auth.service";
import { firstValueFrom, from, Observable, of, Subscription, throwError } from "rxjs";
import { catchError, map, retry, take } from "rxjs/operators";
import { environment } from "src/environments/environment";
import { HttpClient } from "@angular/common/http";
import { v4 as uuidv4 } from 'uuid';
import { CommonVariable } from "../app.constants";
import { PaymentService } from "./payment.service";
import { CheckoutComService } from "../pages/sales/payment/services/checkout-com-service";
import { getAuth } from "firebase/auth";
import { collection, onSnapshot, Firestore } from "@angular/fire/firestore";
import { getFunctions, httpsCallable } from "firebase/functions";
import { SalesJourneyIdentityService } from "./sales-journey-identity.service";

// ---------------------------------------------------------------------------
// Checkout.com Flow SDK — loaded lazily via loadScript() on checkout routes
// ---------------------------------------------------------------------------

declare global {
  interface Window {
    CheckoutWebComponents?: (config: CheckoutWebComponentsConfig) => Promise<CheckoutWebComponentsInstance>;
  }
}
export interface CheckoutComFlowHost {
  isDestroyed: boolean;
  checkOutComData: Data;
  pathString: any;
  isCheckoutCom: boolean;
  errorMessage: string;
  showCheckOutComSpinner: boolean;
  addingCard: boolean;
  addNewCardInProgress: boolean;
  showAddNewCard: boolean;
  keys: Data;
  isCheckoutComform: boolean;
  isButtonDisabled: boolean;
  language: string;
  cardTokenizedCalled: boolean;
  timerOn: boolean;
  proceed: boolean;
  isPaymentDone: boolean;
  customerPurchaseEncryptedId?: string;
  getCheckOutComResponseSubscription?: Subscription;
  redirectlink: Data;
  purchaseDetail: Data;
  timerspan: Data;
  timeOut: boolean;
  count: number;
  checkoutFramesInitSeq: number;
  /** Set by loadCheckoutComFlow; used so stale releaseHost cannot tear down a newer session. */
  checkoutFlowSessionId?: number;
  checkoutFramesInitCancelled: boolean;
  checkoutIframeActivationInterval: ReturnType<typeof setInterval> | null;
  checkoutIframeRetryTimer: ReturnType<typeof setTimeout> | null;
  checkoutIframeReloadTimer: ReturnType<typeof setTimeout> | null;
  checkoutIframeRetryAttempted: boolean;
  checkoutFlowReady: boolean;
  readonly checkoutIframeReloadGuardKey: string;
  checkoutFramesInitFrame: number | null;
  generateNewUUID: () => void;
  /** Optional — only hosts that remount Flow after config load need this. */
  initializeCheckoutFlowIFrames?: () => void;
  onCheckoutFlowReady: () => void;
  handleCheckoutFlowError?: (error: Data, resetUi?: boolean) => void;
  onCheckoutFlowPaymentCompleted?: () => void;
  onValidationChanged: (event: Data) => void;
  onCardTokenized: (event: Data) => void;
  resetPaymentSubscriptions: () => void;
  checkStickyUiForProsub: () => void;
  callInt2: () => void | Promise<void>;
  syncPaymentLogToFireStore: () => void;
  paymentErrorMessage: () => void;
  actRoute: { snapshot: { queryParams: Data } };
  paymentMode: string;
  checkoutFlowService: CheckoutFlowService;
  userId: string;
  preferredCurrency: string;
  addonTotalAmount: number;
  paymentReason: string;
  fourCJourneyState?: Data | null;
  getCheckCardDetailId: () => Data | undefined;
  getCookie: (name: string) => string | undefined;
  /** Prefer host journey-aware resolver so repeat users do not send stale anonymous_id. */
  resolveAnonymousIdForPaymentPayload?: () => string | null;
  /** Optional — not all checkout hosts carry alt-prosub context. */
  altprosubDetails?: Data;
  /** Optional — hosts that continue post-success navigation implement this. */
  proceedAfterCheckoutSuccess?: (user: Data | null, paymentResponse: Data) => void;
  /** Used by onCardTokenized / stored-card wait path */
  checkoutSessionExpiredHandled: boolean;
  GTMSegment: { fourxCtaClicked: () => void };
  isCheckoutResponseReceived: boolean;
  fixFooter: boolean;
  token: string;
  ensureCheckoutSessionValid: () => Promise<boolean>;
  handleCheckoutSessionExpired: (error?: Data) => void;
  loadingService: { present: () => void; dismiss: () => void };
  checkoutPaymentFailedFlow: () => void;
  sanitizer: { bypassSecurityTrustResourceUrl: (url: string) => Data };
  paymentHold: boolean;
  isFourCCheckoutRoute: () => boolean;
  showFourCAlertFromMappedCheckoutStatus: (payment_status: Data) => void | Promise<void>;
  firestore: Firestore;
}
interface CheckoutWebComponentsConfig {
  paymentSession?: Data;
  publicKey: string;
  environment: "sandbox" | "production";
  locale?: string;
  componentOptions?: Data;
  handleSubmit?: (component: Data, submitData: Data) => Promise<Data>;
  onReady?: (component: Data) => void;
  onTokenized?: (component: Data, result: Data) => Promise<{ continue: true } | { continue: false; errorMessage?: string }>;
  onPaymentCompleted?: (component: Data, payment: Data) => void;
  onError?: (component: Data, error: Data) => void;
}

interface CheckoutWebComponentsInstance {
  create: (type: string, options?: Data) => CheckoutFlowComponent;
}

interface CheckoutFlowComponent {
  mount: (selector: string | HTMLElement) => void;
  unmount?: () => void;
  submit?: () => void | Promise<void>;
  isAvailable?: () => Promise<boolean>;
}
export interface CheckoutFlowRedirectReturn {
  isRedirectReturn: boolean;
  status?: string;
  logId?: string;
  userId?: string;
  paymentId?: string;
}
// ---------------------------------------------------------------------------
// Public types — consumed by payment surfaces (payment.page, add-card, etc.)
// ---------------------------------------------------------------------------

export interface CheckoutFlowComponentOptions {
  /** Internal guard only; never forwarded as an SDK option. */
  cardVerification?: boolean;
  amount: number;
  currency: string;
  processingChannelId: string;
  data?: { billingCountry?: string };
}

export interface CheckoutFlowMountOptions {
  container: string | HTMLElement;
  publicKey: string;
  environment: "sandbox" | "production";
  paymentSession: Data;
  locale?: string;
  componentOptions?: CheckoutFlowComponentOptions;
  onReady?: () => void;
  onPaymentCompleted?: (component: Data, payment: Data) => void;
  onError?: (component: Data, error: Data) => void;
}

export interface CheckoutFlowCardEntryOptions {
  container: string | HTMLElement;
  publicKey: string;
  paymentSession?: Data;
  environment: "sandbox" | "production";
  locale?: string;
  componentOptions: CheckoutFlowComponentOptions;
  /** Creates a display-only payment session so Flow can render card fields on load. */
  createDisplaySession?: (attempt?: number) => Promise<Data>;
  onTokenized?: (component: Data, result: Data) => Promise<{ continue: true } | { continue: false; errorMessage?: string }>;
  onReady?: () => void;
  onPaymentCompleted?: (component: Data, payment: Data) => void;
  onError?: (component: Data, error: Data) => void;
}

export interface CheckoutFlowRedirectReturn {
  isRedirectReturn: boolean;
  status?: string;
  logId?: string;
  userId?: string;
  paymentId?: string;
}

// ---------------------------------------------------------------------------
// Shared constants & session payload helpers
// ---------------------------------------------------------------------------

/** Payment methods exposed in Flow — card only for this integration. */
export const CHECKOUT_FLOW_CARD_ONLY_METHODS = ["card"] as const;

/** Query params appended by Checkout.com after 3DS redirect to return_url. */
export const CHECKOUT_FLOW_RETURN_QUERY_KEYS = ["status", "logId", "userId"] as const;

/**
 * Tracks the validated @checkout.com/checkout-web-components release.
 * Checkout.com CDN serves the latest build at index.js (no versioned CDN path).
 */
export const CHECKOUT_FLOW_SDK_VERSION = "1.11.0";

export const CHECKOUT_FLOW_SCRIPT_URL = "https://checkout-web-components.checkout.com/index.js";

/** Resolves the device fingerprint visitor ID for payment payloads. */
export function resolvePaymentVisitorId(): string {
  return CommonVariable.FingerprintJSConst || localStorage.getItem("fingerSalt") || "";
}

/** Ensures createcheckoutflowsessionv2 only enables card (hides Google Pay, UPI, etc.). */
export function applyCardOnlyFlowSessionPayload(paymentData: Record<string, unknown>): Record<string, unknown> {
  return {
    ...paymentData,
    enabled_payment_methods: paymentData["enabled_payment_methods"] ?? [...CHECKOUT_FLOW_CARD_ONLY_METHODS],
  };
}

// ---------------------------------------------------------------------------
// Service
// ---------------------------------------------------------------------------

@Injectable({
  providedIn: "root",
})
export class CheckoutFlowService implements OnDestroy {
  private scriptLoadPromise: Promise<void> | null = null;
  private flowComponent: CheckoutFlowComponent | null = null;
  private checkoutInstance: CheckoutWebComponentsInstance | null = null;
  private http = inject(HttpClient);
  private paymentService = inject(PaymentService);
  private configLoadSubscription: Subscription | null = null;
  private paymentActionSubscriptions = new Subscription();
  /** Bumps on each loadCheckoutComFlow so a stale releaseHost cannot cancel a newer session. */
  private flowSessionId = 0;
  /** In-memory dedupe for checkout response handling. */
  private processedCheckoutResponseKeys = new Set<string>();
  private checkoutComService = inject(CheckoutComService);
  /** Abort token: invalidated by unmount() so any in-flight mountForCardEntry() exits early. */
  private mountToken: symbol | null = null;
  /** How long to wait for CheckoutWebComponents onReady before retrying. Tests override this. */
  private checkoutFlowReadyTimeoutMs = 15000;
  private cancelReadyWait: (() => void) | null = null;
  private salesJourneyIdentityService = inject(SalesJourneyIdentityService);

  constructor(private authService: AuthService) {}

  ngOnDestroy(): void {
    this.releaseHost();
  }

  // -------------------------------------------------------------------------
  // Environment & session configuration
  // -------------------------------------------------------------------------

  /** Resolves the callable used to create a Payment Session server-side. */
  /** Resolves the callable used to create a Payment Session server-side. */
  getSessionCloudFunctionName(_paymentMode?: string): string {
    return environment.cloudFunctions.createCheckoutFlowSession;
  }

  isSandboxPaymentMode(paymentMode?: string | null): boolean {
    return paymentMode === "sandbox";
  }

  getFlowEnvironment(paymentMode?: string, publicKey?: string): "sandbox" | "production" {
    if (paymentMode) {
      const mode = paymentMode.toLowerCase();
      if (mode === "sandbox" || mode === "test") {
        return "sandbox";
      }
      if (mode === "live" || mode === "production") {
        return "production";
      }
    }

    return publicKey?.startsWith("pk_sbox_") ? "sandbox" : "production";
  }

  /** Maps existing Frames localization codes to Flow locale strings. */
  mapLocale(language: string): string {
    const localeMap: Record<string, string> = {
      "EN-GB": "en-GB",
      "DE-DE": "de-DE",
      "ES-ES": "es-ES",
      "FR-FR": "fr-FR",
      "IT-IT": "it-IT",
    };
    return localeMap[language] || "en-GB";
  }

  /**
   * Full current page URL (path + query string) for post-payment redirect.
   * Passed as return_url on createcheckoutflowsessionv2.
   */
  getCurrentPageReturnUrl(): string {
    if (typeof window === "undefined") {
      return "";
    }
    return window.location.href;
  }

  /** Builds the PWA `/r` callback URL passed to the backend for legacy post-3DS browser return. */
  buildFlowReturnUrl(origin?: string, sourceSite?: string): string {
    const base = origin || (sourceSite ? `https://${sourceSite}` : window.location.origin);
    return `${base}/r`;
  }

  /**
   * Builds Flow componentOptions from Firestore checkout credentials and payment context.
   * processingChannelId is read from credentials_uat / credentials on p_type 21.
   */
  buildComponentOptions(
    keys: Data,
    amount: number | string,
    currency: string,
    billingCountry?: string,
    cardVerification = false,
  ): CheckoutFlowComponentOptions {
    const processingChannelId =
      keys?.processingChannelId ??
      keys?.processing_channel_id ??
      keys?.processing_channel ??
      keys?.channelId ??
      keys?.channel_id ??
      keys?.pcid ??
      "";

    const rawAmount = typeof amount === "string" ? parseFloat(amount) : Number(amount);
    const amountMinor = Number.isFinite(rawAmount) && rawAmount > 0 ? Math.round(rawAmount * 100) : 0;

    const explicitZero = amount === 0 ||
      (typeof amount === "string" && /^0+(\.0+)?$/.test(amount.trim()));
    if (cardVerification ? !explicitZero || currency !== "GBP" : !amountMinor) {
      throw new Error(`Invalid checkout amount: ${amount}`);
    }

    return {
      ...(cardVerification ? { cardVerification: true } : {}),
      amount: amountMinor,
      currency: String(currency || "GBP").toUpperCase(),
      processingChannelId: String(processingChannelId),
      data: {
        billingCountry: billingCountry ?? keys?.billingCountry ?? keys?.billing_country ?? "GB",
      },
    };
  }

  /** Convenience wrapper when amount/currency are already on the session payload. */
  buildComponentOptionsFromPaymentData(keys: Data, paymentData: Record<string, unknown>): CheckoutFlowComponentOptions {
    // This function is used to build the checkout flow component options from the payment data
    return this.buildComponentOptions(
      keys,
      paymentData["amount"] as number | string,
      String(paymentData["currency"] ?? "GBP"),
      paymentData["billing_country"] as string | undefined,
      paymentData["card_verification"] === true,
    );
  }

  // -------------------------------------------------------------------------
  // Payment session — create & normalize
  // -------------------------------------------------------------------------

  /**
   * Calls createcheckoutflowsessionv2 (or sandbox variant).
   * Automatically includes return_url with the current page URL and card-only methods.
   */
  createFlowSession(paymentMode: string, paymentData: Record<string, unknown>): Observable<unknown> {
    const functionName = this.getSessionCloudFunctionName(paymentMode);
    const returnUrl = (paymentData["return_url"] as string) ?? window.location.href;
    const payload = {
      data: applyCardOnlyFlowSessionPayload({
        ...paymentData,
        return_url: encodeURIComponent(returnUrl),
        sandbox: this.isSandboxPaymentMode(paymentMode),
      }),
    };
    const region = AuthService.FIREBASE_REGION || "us-central1";
    const callable = httpsCallable(getFunctions(undefined, region), functionName);

    return from(callable(payload)).pipe(
      map((res: Data) => {
        const data = res?.data;
        const errorCode = String(data?.code ?? data?.result?.code ?? "").toLowerCase();
        if (errorCode.includes("permission-denied")) {
          throw Object.assign(new Error("Sandbox access denied."), { code: "permission-denied" });
        }
        return data;
      }),
      catchError((error) => {
        this.authService.errorHandlerLogs("createcheckoutflowsession failed", error);
        const code = String(error?.code ?? "").toLowerCase();
        if (code.includes("permission-denied")) {
          return throwError(() => Object.assign(new Error("Sandbox access denied."), { code: "permission-denied" }));
        }
        return throwError(() => error);
      }),
    );
  }

  /**
   * Normalizes createcheckoutflowsessionv2 response into a Payment Session
   * accepted by CheckoutWebComponents.mount().
   */
  extractPaymentSession(response: Data | null | undefined): Data | null {
    if (!response) {
      return null;
    }
    try {
      return this.normalizePaymentSession(response);
    } catch {
      return null;
    }
  }

  // -------------------------------------------------------------------------
  // Mount / unmount / submit
  // -------------------------------------------------------------------------

  /**
   * Mount Flow when a payment session already exists (store add-card, try-again, etc.).
   */
  async mount(options: CheckoutFlowMountOptions): Promise<void> {
    await this.loadScript();
    this.assertSdkAvailable();

    this.unmount();

    this.checkoutInstance = await window.CheckoutWebComponents!({
      paymentSession: options.paymentSession,
      publicKey: options.publicKey,
      environment: options.environment,
      locale: options.locale ?? "en-GB",
      componentOptions: this.getFlowSdkComponentOptions(),
      onReady: () => options.onReady?.(),
      onPaymentCompleted: (component, payment) => options.onPaymentCompleted?.(component, payment),
      onError: (component, error) => options.onError?.(component, error),
    });

    this.flowComponent = this.checkoutInstance.create("card");
    this.flowComponent.mount(options.container);
  }

  /**
   * Mount Flow for card entry on the payment page.
   * Uses paymentSession when provided, otherwise createDisplaySession on load.
   */
  async mountForCardEntry(options: CheckoutFlowCardEntryOptions): Promise<void> {
    this.validateCardEntryOptions(options);
    this.unmount();

    // Stamp a unique token for this mount operation. If unmount() is called at any point
    // during the async sequence (e.g. Angular destroys the host component while the payment
    // session is loading), the token is invalidated and we exit before mounting the
    // Checkout.com SDK into a detached DOM node — which would corrupt its internal Preact
    // hook state and cause "TypeError: undefined is not an object (evaluating 'r.c=e')".
    let token = Symbol("mountForCardEntry");
    this.mountToken = token;

    try {
      await this.loadScript();
      this.assertMountActive(token);
      this.assertSdkAvailable();
      const mountTarget = await this.resolveMountTarget(options.container);
      this.assertMountActive(token);
      const paymentSession = await this.resolveCardEntryPaymentSession(options);
      this.assertMountActive(token);

      try {
        await this.initializeFlowInstance({ options, mountTarget, paymentSession, token });
      } catch (error) {
        this.assertMountActive(token);
        const readyTimeout = (error as Data)?.code === "checkout_flow_ready_timeout";
        if (readyTimeout && (!options.createDisplaySession || options.paymentSession)) {
          throw error;
        }
        if (!readyTimeout && !this.isPaymentSessionRequiredError(error)) {
          throw error;
        }
        this.unmount();
        token = Symbol("mountForCardEntryRetry");
        this.mountToken = token;
        try {
          // A readiness timeout gets a fresh display session when a factory is available.
          // Preserve the existing same-session retry for synchronous SDK session errors.
          const retrySession = readyTimeout
            ? await this.resolveCardEntryPaymentSession(options, 2)
            : paymentSession;
          this.assertMountActive(token);
          await this.initializeFlowInstance({ options, mountTarget, paymentSession: retrySession, token });
          this.authService.errorHandlerLogs("checkout_flow_retry_succeeded", { attempt: 2 });
        } catch (retryError) {
          this.assertMountActive(token);
          this.authService.errorHandlerLogs("checkout_flow_retry_failed", { attempt: 2 });
          throw retryError;
        }
      }
    } catch (error) {
      if (this.mountToken === token) {
        this.unmount();
      }
      throw error;
    } finally {
      // mountToken represents an in-flight mount only; do not clear a newer mount's token.
      if (this.mountToken === token) {
        this.mountToken = null;
      }
    }
  }

  /**
   * Trigger payment submission (equivalent to Frames.submitCard()).
   * Call from the Pay / Add Card button after the user enters card details.
   * @returns false when Flow is not mounted/ready so the host can show loading feedback.
   */
  submitPayment(): boolean {
    if (!this.flowComponent?.submit) {
      return false;
    }
    this.flowComponent.submit();
    return true;
  }

  unmount(): void {
    // Invalidate any in-flight mountForCardEntry() so it exits before mounting into a
    // detached DOM node (which corrupts the Checkout.com SDK's internal Preact hook state).
    this.mountToken = null;
    this.cancelReadyWait?.();
    this.cancelReadyWait = null;
    if (this.flowComponent?.unmount) {
      try {
        this.flowComponent.unmount();
      } catch {
        // Best-effort cleanup; component may already be torn down after redirect.
      }
    }
    this.flowComponent = null;
    this.checkoutInstance = null;
  }

  // -------------------------------------------------------------------------
  // Post-3DS redirect — return_url query params
  // -------------------------------------------------------------------------

  /** Returns true when the page was loaded after a Checkout Flow browser redirect. */
  handleRedirectReturn(queryParams: Data): boolean {
    return this.parseRedirectReturn(queryParams).isRedirectReturn;
  }

  /** Parses post-3DS query params on return_url (status, logId, userId). */
  parseRedirectReturn(queryParams: Data): CheckoutFlowRedirectReturn {
    const logId = queryParams?.["logId"];
    const userId = queryParams?.["userId"];
    const status = queryParams?.["status"];
    const isRedirectReturn = Boolean(status && (status || logId || userId));

    return {
      isRedirectReturn,
      status: status ? String(status).toLowerCase() : undefined,
      logId: logId ? String(logId) : undefined,
      userId: userId ? String(userId) : undefined,
    };
  }

  /** Strips Checkout return params from a query-param map (for building a clean return_url). */
  stripRedirectReturnParams(queryParams: Record<string, unknown>): Record<string, unknown> {
    const cleaned = { ...queryParams };
    for (const key of CHECKOUT_FLOW_RETURN_QUERY_KEYS) {
      delete cleaned[key];
    }
    return cleaned;
  }

  // -------------------------------------------------------------------------
  // Private — SDK instance lifecycle
  // -------------------------------------------------------------------------

  private assertMountActive(token: symbol): void {
    if (this.mountToken !== token) {
      throw Object.assign(new Error("Checkout form mounting cancelled"), { code: "checkout_flow_mount_cancelled" });
    }
  }

  private async initializeFlowInstance(params: {
    options: CheckoutFlowCardEntryOptions;
    mountTarget: string | HTMLElement;
    paymentSession?: Data;
    token: symbol;
  }): Promise<void> {
    const { options, mountTarget, paymentSession, token } = params;

    if (!paymentSession?.id) {
      throw new Error("Checkout payment session is required");
    }

    this.assertMountActive(token);
    let active = true;
    let settled = false;
    let mounted = false;
    let readyReported = false;
    let resolveReady!: () => void;
    let rejectReady!: (error: unknown) => void;
    const ready = new Promise<void>((resolve, reject) => {
      resolveReady = resolve;
      rejectReady = reject;
    });
    const finish = (error?: unknown) => {
      if (settled) { return; }
      settled = true;
      clearTimeout(timer);
      if (error !== undefined) {
        active = false;
        rejectReady(error);
      } else {
        try {
          options.onReady?.();
          resolveReady();
        } catch (callbackError) {
          active = false;
          rejectReady(callbackError);
        }
      }
    };
    const cancel = () => {
      active = false;
      finish(Object.assign(new Error("Checkout form mounting cancelled"), { code: "checkout_flow_mount_cancelled" }));
    };
    const timer = setTimeout(() => finish(Object.assign(
      new Error("Secure card form did not load. Please try again."),
      { code: "checkout_flow_ready_timeout" },
    )), this.checkoutFlowReadyTimeoutMs);
    this.cancelReadyWait = cancel;

    const initialize = async () => {
      const checkoutInstance = await window.CheckoutWebComponents!({
        paymentSession,
        publicKey: options.publicKey,
        environment: options.environment,
        locale: options.locale ?? "en-GB",
        componentOptions: this.buildMergedComponentOptions(options),
        ...(options.onTokenized ? {
          onTokenized: async (component: Data, result: Data) => {
            if (!active) { return { continue: false as const }; }
            const acceptance = await options.onTokenized!(component, result);
            return active ? acceptance : { continue: false as const };
          },
        } : {}),
        onReady: () => {
          if (!active || readyReported) { return; }
          readyReported = true;
          if (mounted) { finish(); }
        },
        onPaymentCompleted: (component, payment) => {
          if (active) { options.onPaymentCompleted?.(component, payment); }
        },
        onError: (component, error) => {
          if (!active) { return; }
          finish(error ?? new Error("Checkout form initialization failed"));
          options.onError?.(component, error);
        },
      });
      if (!active || this.mountToken !== token) { return; }
      this.checkoutInstance = checkoutInstance;
      await this.createAndMountCardComponent(mountTarget, token);
      if (!active || this.mountToken !== token) { return; }
      mounted = true;
      if (readyReported) { finish(); }
    };
    try {
      await Promise.all([initialize(), ready]);
    } catch (error) {
      active = false;
      finish(error);
      throw error;
    } finally {
      clearTimeout(timer);
      if (!active && this.cancelReadyWait === cancel) {
        this.cancelReadyWait = null;
      }
    }
  }

  /** Card-only UI options passed to CheckoutWebComponents (hides APM selector). */
  private getFlowSdkComponentOptions(): Data {
    return {
      flow: {
        expandFirstPaymentMethod: true,
      },
      card: {
        displayCardholderName: "top",
      },
    };
  }

  /** Merges amount/currency/channel from the payment payload with card-only UI options. */
  private buildMergedComponentOptions(options: CheckoutFlowCardEntryOptions): Data {
    const uiOptions = this.getFlowSdkComponentOptions();
    const businessOptions = options.componentOptions;

    return {
      amount: businessOptions.amount,
      currency: businessOptions.currency,
      processingChannelId: businessOptions.processingChannelId,
      data: businessOptions.data,
      flow: uiOptions.flow,
      card: uiOptions.card,
    };
  }

  private async createAndMountCardComponent(
    mountTarget: string | HTMLElement,
    token: symbol
  ): Promise<void> {
    if (this.mountToken !== token) { return; }

    let flowComponent: CheckoutFlowComponent;
    try {
      flowComponent = this.checkoutInstance!.create("card");
    } catch (error) {
      if (this.mountToken === token) {
        this.checkoutInstance = null;
      }
      throw error;
    }

    if (this.mountToken !== token) {
      this.abandonStaleFlowComponent(flowComponent);
      return;
    }
    this.flowComponent = flowComponent;

    if (flowComponent.isAvailable) {
      const available = await flowComponent.isAvailable();
      if (this.mountToken !== token) {
        this.abandonStaleFlowComponent(flowComponent);
        return;
      }
      if (!available) {
        throw new Error("Checkout Flow component is not available");
      }
    }

    if (this.mountToken !== token) {
      this.abandonStaleFlowComponent(flowComponent);
      return;
    }
    flowComponent.mount(mountTarget);

    if (this.mountToken !== token) {
      this.abandonStaleFlowComponent(flowComponent);
    }
  }

  /** Unmount a locally created SDK component without invalidating a newer in-flight mount. */
  private abandonStaleFlowComponent(flowComponent: CheckoutFlowComponent): void {
    try {
      flowComponent.unmount?.();
    } catch {
      // Best-effort cleanup; component may already be torn down.
    }
    if (this.flowComponent === flowComponent) {
      this.flowComponent = null;
    }
  }

  private assertSdkAvailable(): void {
    if (!window.CheckoutWebComponents) {
      throw new Error("CheckoutWebComponents is not available");
    }
  }

  // -------------------------------------------------------------------------
  // Private — payment session resolution
  // -------------------------------------------------------------------------

  private async resolveCardEntryPaymentSession(
    options: CheckoutFlowCardEntryOptions,
    attempt = 1
  ): Promise<Data> {
    if (options.paymentSession) {
      return this.normalizePaymentSession(options.paymentSession);
    }

    if (options.createDisplaySession) {
      return this.normalizePaymentSession(await options.createDisplaySession(attempt));
    }

    throw new Error("Checkout Flow requires paymentSession or createDisplaySession");
  }

  private normalizePaymentSession(raw: Data): Data {
    if (!raw || typeof raw !== "object") {
      throw new Error("Invalid payment session response from server");
    }

    if (raw.id) {
      return raw;
    }

    const nested = raw.payment_session ?? raw.paymentSession ?? raw.session ?? raw.data;
    if (nested?.id) {
      return nested;
    }

    throw new Error("Payment session response missing id");
  }

  

  

  

  

  

  private isPaymentSessionRequiredError(error: unknown): boolean {
    const message = String((error as Data)?.message ?? error ?? "");
    // Match explicit SDK error strings OR very short messages (≤4 chars) that are
    // minified identifiers leaking from the minified Checkout.com Web Components SDK
    // (e.g. "Aa" — LOTTOSOCIAL-PWA-1N6H). The SDK is documented to sometimes reject
    // the first mount attempt; we treat these as safe to retry once.
    return (
      message.includes("payment_session_required") ||
      message.includes("PaymentSession is required") ||
      (message.length > 0 && message.length <= 4 && /^[A-Za-z]+$/.test(message))
    );
  }

  // -------------------------------------------------------------------------
  // Private — validation & DOM
  // -------------------------------------------------------------------------

  private validateCardEntryOptions(options: CheckoutFlowCardEntryOptions): void {
    if (!options.publicKey) {
      throw new Error("Checkout publicKey is not configured");
    }

    const { amount, currency, processingChannelId } = options.componentOptions;
    if (!processingChannelId) {
      throw new Error("Checkout processingChannelId is not configured");
    }
    if (!currency) {
      throw new Error("Checkout currency is not configured");
    }
    if (!Number.isFinite(amount) || (options.componentOptions.cardVerification === true
      ? amount !== 0 || currency !== "GBP" : amount <= 0)) {
      throw new Error(`Checkout amount is invalid: ${amount}`);
    }
  }

  /** Waits for the mount container to exist in the DOM (Angular view init timing). */
  private async resolveMountTarget(container: string | HTMLElement): Promise<string | HTMLElement> {
    if (typeof container !== "string") {
      return container;
    }

    for (let attempt = 0; attempt < 30; attempt++) {
      if (document.querySelector(container)) {
        return container;
      }
      await new Promise((resolve) => requestAnimationFrame(resolve));
    }

    throw new Error(`Checkout container not found: ${container}`);
  }

  // -------------------------------------------------------------------------
  // Private — script loading
  // -------------------------------------------------------------------------

  /** Preloads the Checkout Flow SDK so card fields mount faster on payment pages. */
  preloadSdk(): Promise<void> {
    return this.loadScript();
  }

  private loadScript(): Promise<void> {
    if (window.CheckoutWebComponents) {
      return Promise.resolve();
    }

    if (this.scriptLoadPromise) {
      return this.scriptLoadPromise;
    }

    this.scriptLoadPromise = new Promise<void>((resolve, reject) => {
      const existing = document.querySelector(`script[src="${CHECKOUT_FLOW_SCRIPT_URL}"]`);
      const script = existing || document.createElement("script");
      const cleanup = () => {
        script.removeEventListener("load", onLoad);
        script.removeEventListener("error", onError);
      };
      const onLoad = () => { cleanup(); resolve(); };
      const onError = () => {
        cleanup();
        script.remove();
        reject(new Error("Failed to load Checkout Flow script"));
      };
      script.addEventListener("load", onLoad);
      script.addEventListener("error", onError);
      if (!existing) {
        (script as HTMLScriptElement).src = CHECKOUT_FLOW_SCRIPT_URL;
        (script as HTMLScriptElement).async = true;
        document.head.appendChild(script);
      }
    }).catch(error => {
      this.scriptLoadPromise = null;
      throw error;
    });

    return this.scriptLoadPromise;
  }

  public async mountCheckoutFlowUi(
    host: any,
    paymentData: Data,
    initSequence: number
  ): Promise<void> {
    if (host.isDestroyed || initSequence !== host.checkoutFramesInitSeq) {
      return;
    }

    const componentOptions = this.buildComponentOptionsFromPaymentData(
      host.keys,
      paymentData as Record<string, unknown>
    );
    let paymentSessionId: string | undefined;

    // [CheckoutFlow FE#3] Keep onPaymentCompleted — show processing, wait for Firestore (do not fulfil here).
    await this.mountForCardEntry({
      container: '#cko-flow-container',
      publicKey: host.keys.publicKey,
      environment: this.getFlowEnvironment(host.paymentMode, host.keys.publicKey),
      locale: this.mapLocale(host.language || 'EN-GB'),
      componentOptions,
      createDisplaySession: async () => {
        const session = await this.createCheckoutFlowDisplaySession(host, paymentData);
        if (host.isDestroyed || initSequence !== host.checkoutFramesInitSeq) {
          throw new Error("Checkout attempt changed");
        }
        paymentSessionId = session?.id;
        return session;
      },
      ...(paymentData.card_verification === true ? {
        onTokenized: async (): Promise<{ continue: true } | { continue: false; errorMessage?: string }> => {
          const isCurrentAttempt = () => !host.isDestroyed && !host.timeOut && !host.checkoutFramesInitCancelled &&
            initSequence === host.checkoutFramesInitSeq &&
            host.pathString === paymentData.logId && host.userId === paymentData.userId;
          if (!isCurrentAttempt() || !paymentSessionId?.startsWith("ps_")) return { continue: false };
          try {
            await this.recordRecurringConsent(host.paymentMode, paymentData, paymentSessionId);
            return isCurrentAttempt() ? { continue: true } : { continue: false };
          } catch {
            return { continue: false, errorMessage: "Unable to continue. Please try again." };
          }
        },
      } : {}),
      onReady: () => this.onCheckoutIframeReady(host),
      onPaymentCompleted: (_self, paymentResponse) => {
        const params = new URLSearchParams({
          status: "success",
          logId: host.pathString ?? localStorage.getItem("myuuid"),
          userId: host.userId ?? localStorage.getItem("anonymousId"),
          "cko-payment-id": paymentResponse.id,
        });
        if (this.isSandboxPaymentMode(host.paymentMode)) params.set("sandbox", "true");
        window.location.href =
       `${environment.onSuccessRedirectUrl}/${environment.cloudFunctions.checkoutFlowResponseHandler}?${params}`;
      },
      onError: (_self, error) => {
        // The consent hook aborts before a provider payment exists; keep the form available for retry.
        if (paymentData.card_verification === true && error?.code === "payment_request_aborted" &&
            !error?.details?.paymentId) return;
        const paymentId = error?.details?.paymentId;
       const params = new URLSearchParams({
          status: "fail",
          logId: host.pathString ?? localStorage.getItem("myuuid"),
          userId: host.userId ?? localStorage.getItem("anonymousId"),
          "cko-payment-id": paymentId,
        });
        if (this.isSandboxPaymentMode(host.paymentMode)) params.set("sandbox", "true");
        window.location.href =
          `${environment.onSuccessRedirectUrl}/${environment.cloudFunctions.checkoutFlowResponseHandler}?${params}`;
      },
    });

    if (host.isDestroyed || initSequence !== host.checkoutFramesInitSeq) {
      host.checkoutFlowService.unmount();
    }
  }

  private onCheckoutIframeReady(host: CheckoutComFlowHost): void {
    host.checkoutFlowReady = true;
    this.clearCheckoutIframeRecovery(host);
    host.onCheckoutFlowReady();
    host.showCheckOutComSpinner = false;
    if (host.errorMessage === "Loading secure card form...") {
      host.errorMessage = "";
    }
  }

  /** [CheckoutFlow FE#4] Validate msn, purchaseDetail, amount and currency before mounting Flow. */
  private async recordRecurringConsent(paymentMode: string, paymentData: Data, sessionId: string): Promise<void> {
    const response = await firstValueFrom(this.createFlowSession(paymentMode, {
      action: "record_recurring_consent",
      userId: paymentData.userId,
      logId: paymentData.logId,
      payment_session_id: sessionId,
      recurring_consent: {
        accepted: true,
        version: "pay-with-card-v2",
        user_id: paymentData.userId,
        log_id: paymentData.logId,
        prosub_id: String(paymentData.prosub_id),
        offer_id: String(paymentData.offer_id),
      },
    })) as Data;
    if (response?.accepted !== true || response.version !== "pay-with-card-v2" ||
        response.payment_session_id !== sessionId) {
      throw new Error("Recurring acceptance was not saved");
    }
  }

  public validateCheckoutFlowPrerequisites(host: CheckoutComFlowHost): string | null {
    const msn = host.checkOutComData?.msn ?? localStorage.getItem("msn");
    const currency = host.preferredCurrency;
    const amount =
      host.purchaseDetail?.addons?.length > 0
        ? host.addonTotalAmount
        : (host.purchaseDetail?.offers?.int_price?.[host.preferredCurrency] ??
          host.purchaseDetail?.offers?.price ??
          host.purchaseDetail?.prize);

    if (!msn || !host.purchaseDetail || amount == null || amount === "" || !currency) {
      return "Missing required checkout data";
    }
    return null;
  }

  private async createCheckoutFlowDisplaySession(host: CheckoutComFlowHost, paymentData: Data): Promise<Data> {
    const response = await firstValueFrom(
      this.createFlowSession(host.paymentMode, paymentData as Record<string, unknown>),
    );
    // This function is used to extract the payment session from the checkout flow response
    const session = this.extractPaymentSession(response as Data);
    if (!session) {
      this.authService.errorHandlerLogs("Failed to create checkout flow display session");
    }
    return session;
  }

  buildCheckoutFlowPaymentData(host: CheckoutComFlowHost): Data {
    // Journey-aware: repeat DFCI users must not carry a stale localStorage anonymousId.
    const anonymousId = this.resolveCheckoutAnonymousId(host);
    const userMsn = localStorage.getItem("msn");
    const storedCountryCode = localStorage.getItem("country_code");
    const msn = host.checkOutComData?.msn ?? userMsn;
    const currency = host.preferredCurrency;
    const amount =
      host.purchaseDetail?.addons?.length > 0
        ? host.addonTotalAmount
        : (host.purchaseDetail?.offers?.int_price?.[host.preferredCurrency] ??
          host.purchaseDetail?.offers?.price ??
          host.purchaseDetail?.prize);
    if (!msn || !host.purchaseDetail || amount == null || amount === "" || !currency) {
      throw new Error("Missing required checkout data");
    }

    const countryCode = host.checkOutComData?.country_code ?? storedCountryCode ?? "";
    const purchaseDetail = { ...host.purchaseDetail, ...(host.getCheckCardDetailId() ?? {}) };
    const userNumber =
      countryCode && msn.startsWith(countryCode) ? msn.substring(countryCode.length) : msn.substring(2);
    const preferredAmount = host.purchaseDetail?.offers?.int_price?.[host.preferredCurrency]
      ? host.purchaseDetail?.offers?.int_price?.[host.preferredCurrency]?.toFixed(2)
      : host.purchaseDetail?.prize && !isNaN(host.purchaseDetail?.prize)
        ? parseFloat(host.purchaseDetail?.prize)?.toFixed(2)
        : host.checkOutComData?.prize;

    const cardVerification = purchaseDetail?.offers?.card_verification === true;
    if (cardVerification && (
      currency !== "GBP" || purchaseDetail?.offers?.price !== 0 ||
      purchaseDetail?.offers?.int_price?.GBP !== 0 ||
      purchaseDetail?.offers?.show_pre_auth !== false ||
      host.paymentReason !== "offer" || host.purchaseDetail?.addons?.length > 0
    )) {
      throw new Error("Invalid card verification offer");
    }

    const TP1 = localStorage.getItem('TP1') || '';
    const TP2 = localStorage.getItem('TP2') || '';
    const TP3 = localStorage.getItem('TP3') || '';
    const T_ID = localStorage.getItem('T_ID') || '';
    // Default amount for pre-auth
    const defaultAmount = '1.00';
    // Final amount for the payment
    const finalAmount =  purchaseDetail?.offers?.show_pre_auth === true 
    ? defaultAmount 
    :(host.purchaseDetail.addons && host.purchaseDetail.addons.length > 0
    ? host.addonTotalAmount
    : preferredAmount) ;
    const currentUser = getAuth().currentUser;
    const dfciUserId = currentUser?.isAnonymous
      ? null
      : (currentUser?.uid ?? host.userId ?? null);
  // Payment data for the payment
  const paymentData = {
      ...(cardVerification ? { card_verification: true } : {}),
      amount: cardVerification ? 0 : finalAmount,
      currency: host.preferredCurrency,
      email: this.resolveCheckoutEmail(host, msn),
      name: host.checkOutComData?.name,
      phone: msn,
      userId: dfciUserId ?? anonymousId,
      logId: host.pathString,
      msn: {
        country_code: `+${countryCode}`,
        number: userNumber,
      },
      offer_id: purchaseDetail?.offers?.offerid ? purchaseDetail.offers.offerid : purchaseDetail.offer_id,
      prosub_id: purchaseDetail.prosub_id ? purchaseDetail.prosub_id : localStorage.getItem("prosub_id"),
      anonymous_id: anonymousId,
      tp1: TP1 === "null" ? "" : TP1,
      tp2: TP2 === "null" ? "" : TP2,
      tp3: TP3 === "null" ? "" : TP3,
      t_id: T_ID === "null" ? "" : T_ID,
      Other: purchaseDetail.other
        ? purchaseDetail.other
        : localStorage.getItem("Other")
          ? localStorage.getItem("Other")
          : host.getCookie("Other")
            ? host.getCookie("Other")
            : "",
      value: cardVerification ? 0 : purchaseDetail?.offers?.price,
      preferred_currency: {
        ...(cardVerification ? { amount: 0 } : {}),
        Value: cardVerification ? 0 : purchaseDetail.offers?.int_price?.[host.preferredCurrency]
          ? purchaseDetail.offers?.int_price?.[host.preferredCurrency]
          : purchaseDetail?.prize,
        currency: host.preferredCurrency,
      },
      p_type: purchaseDetail?.p_type ? purchaseDetail.p_type : "21",
      visitorid: resolvePaymentVisitorId(),
      requestid: uuidv4(),
      add_on: (purchaseDetail?.addons || [])
        .map((data) => ({
          ...data,
          offer_id: String(data.offer_id),
        }))
        .filter((item, index, self) => index === self.findIndex((t) => t?.offer_id === item?.offer_id)),
      product_description: Array.isArray(purchaseDetail?.offers?.description)
        ? purchaseDetail?.offers?.description?.[0]
        : purchaseDetail?.offers?.description,
      payment_reason: host.paymentReason,
      return_url: window.location.href,
    };
    const paymentDataWithIdentity = this.salesJourneyIdentityService.applyIdentityEnvelope(paymentData, {
      userId: dfciUserId,
      anonymousId,
      // Omit customerId: resolve the actual SQL ID when available.
      msn,
      checkoutAttemptId: host.pathString,
    });
    return paymentDataWithIdentity;
  }

  /**
   * Resolves anonymous_id for checkout Flow payloads.
   * Uses the host's repeat-aware helper when present; never blindly forwards localStorage.
   */
  private resolveCheckoutAnonymousId(host: CheckoutComFlowHost): string | null {
    if (typeof host.resolveAnonymousIdForPaymentPayload === "function") {
      return host.resolveAnonymousIdForPaymentPayload();
    }
    const currentUser = getAuth()?.currentUser;
    return currentUser?.isAnonymous ? currentUser.uid : null;
  }

  /**
   * Resolves the customer email for the Flow session. localStorage can hold the
   * literal strings "undefined"/"null" when the user document has no email, so each
   * candidate is validated before use and an addressable fallback is returned.
   */
  private resolveCheckoutEmail(host: CheckoutComFlowHost, msn: string): string {
    return (
      this.sanitizeEmail(localStorage.getItem("email")) ||
      this.sanitizeEmail(host.checkOutComData?.email) ||
      `${msn}@lottosocial.com`
    );
  }

  private sanitizeEmail(value: unknown): string {
    if (typeof value !== "string") {
      return "";
    }
    const email = value.trim();
    if (email === "" || email.toLowerCase() === "undefined" || email.toLowerCase() === "null") {
      return "";
    }
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email) ? email : "";
  }

  /**
   * Builds Checkout.com msn payload without double "+" or blind digit stripping.
   * country_code is always emitted as "+NN" when present; national number keeps full msn
   * when no usable country prefix can be matched.
   */
  private normalizeCheckoutMsnParts(
    msn: string,
    rawCountryCode: unknown
  ): { country_code: string; number: string } {
    const digitsCountry = String(rawCountryCode ?? "")
      .trim()
      .replace(/^\+/, "");
    const msnStr = String(msn ?? "");
    let number = msnStr;

    if (digitsCountry) {
      if (msnStr.startsWith(`+${digitsCountry}`)) {
        number = msnStr.slice(digitsCountry.length + 1);
      } else if (msnStr.startsWith(digitsCountry)) {
        number = msnStr.slice(digitsCountry.length);
      }
    }

    return {
      country_code: digitsCountry ? `+${digitsCountry}` : "",
      number,
    };
  }


  /** Idempotent replacement for payment-page resetCheckoutCardFrame: unmounts Flow and clears .card-frame DOM. */
  resetCheckoutCardFrame(): void {
    this.unmount();
    this.clearCardFrameDom();
  }

  private clearCardFrameDom(): void {
    if (typeof document === "undefined") return;
    document.querySelectorAll(".card-frame").forEach((el) => {
      el.innerHTML = "";
    });
  }

  /**
   * Drops host-bound callbacks/subscriptions and unmounts Flow.
   * Call from the payment host's ngOnDestroy so the root service does not retain the component.
   * Global teardown runs only when no host is passed (service destroy) or the provided host
   * owns the active flow session, so a stale destroy cannot cancel a newer checkout session.
   */
  releaseHost(host?: CheckoutComFlowHost): void {
    if (host) {
      this.clearCheckoutIframeRecovery(host);
      host.getCheckOutComResponseSubscription?.unsubscribe();
      host.getCheckOutComResponseSubscription = undefined;
    }

    const hostSession = host?.checkoutFlowSessionId;
    // Only tear down global Flow state when no host is provided (service destroy)
    // or the provided host owns the active session. A host without a session id
    // must not cancel a newer Flow mount.
    if (!host || hostSession === this.flowSessionId) {
      this.configLoadSubscription?.unsubscribe();
      this.configLoadSubscription = null;
      this.paymentActionSubscriptions.unsubscribe();
      this.paymentActionSubscriptions = new Subscription();
      this.processedCheckoutResponseKeys.clear();
      this.unmount();
    }
  }

  private isHostAlive(host: CheckoutComFlowHost | null | undefined): boolean {
    return !!host && !host.isDestroyed;
  }

  /**
   * Only https absolute URLs with a validated host may be passed to
   * bypassSecurityTrustResourceUrl (3DS / Checkout redirect iframe).
   * Prefer Checkout.com / known 3DS brokers; other https DNS hosts are allowed
   * for issuer ACS pages, but javascript:/data:/IP/localhost are rejected.
   */
  private isAllowedCheckoutRedirectUrl(rawUrl: unknown): boolean {
    if (typeof rawUrl !== "string" || !rawUrl.trim()) {
      return false;
    }
    let parsed: URL;
    try {
      parsed = new URL(rawUrl.trim());
    } catch {
      return false;
    }
    if (parsed.protocol !== "https:") {
      return false;
    }
    if (parsed.username || parsed.password) {
      return false;
    }
    const hostname = parsed.hostname.toLowerCase();
    if (!hostname || hostname === "localhost" || hostname.endsWith(".localhost")) {
      return false;
    }
    // Block literal IP hosts (common open-redirect / phishing pattern).
    if (/^\d{1,3}(\.\d{1,3}){3}$/.test(hostname) || hostname.includes(":")) {
      return false;
    }
    if (!/^[a-z0-9]([a-z0-9-]*[a-z0-9])?(\.[a-z0-9]([a-z0-9-]*[a-z0-9])?)+$/i.test(hostname)) {
      return false;
    }

    const allowedExact = new Set(["checkout.com"]);
    const allowedSuffixes = [".checkout.com", ".cardinalcommerce.com", ".arcot.com"];
    if (allowedExact.has(hostname) || allowedSuffixes.some((suffix) => hostname.endsWith(suffix))) {
      return true;
    }

    const configured = environment.checkOutUrl;
    if (configured) {
      try {
        const configuredHost = new URL(configured).hostname.toLowerCase();
        if (configuredHost && hostname === configuredHost) {
          return true;
        }
      } catch {
        // ignore invalid environment.checkOutUrl
      }
    }

    // Issuer ACS pages are not always on Checkout.com; https + DNS host already validated.
    return true;
  }

  private trustCheckoutRedirectUrl(host: CheckoutComFlowHost, rawUrl: string): Data | null {
    if (!this.isAllowedCheckoutRedirectUrl(rawUrl)) {
      this.authService.errorHandlerLogs("checkout_flow_redirect_url_rejected", {
        hasUrl: Boolean(rawUrl),
        scheme: (() => {
          try {
            return new URL(String(rawUrl)).protocol;
          } catch {
            return null;
          }
        })(),
      });
      return null;
    }
    return host.sanitizer.bypassSecurityTrustResourceUrl(rawUrl);
  }

  private trackPaymentAction(subscription: Subscription | null | undefined): void {
    if (subscription) {
      this.paymentActionSubscriptions.add(subscription);
    }
  }

  /** Clears residual iframe recovery timers held on the payment host. */
  clearCheckoutIframeRecovery(host: CheckoutComFlowHost): void {
    if (host.checkoutIframeActivationInterval !== null) {
      clearInterval(host.checkoutIframeActivationInterval);
      host.checkoutIframeActivationInterval = null;
    }

    if (host.checkoutIframeRetryTimer !== null) {
      clearTimeout(host.checkoutIframeRetryTimer);
      host.checkoutIframeRetryTimer = null;
    }

    if (host.checkoutIframeReloadTimer !== null) {
      clearTimeout(host.checkoutIframeReloadTimer);
      host.checkoutIframeReloadTimer = null;
    }
  }

  /**
   * Tracks customer purchase after Checkout.com redirect / encrypted CP id.
   * Preserves legacy payment.page success/failure callbacks:
   * handleCheckoutComSuccessPayment({}, purchaseDetail, altprosubDetails, proceedAfterCheckoutSuccess)
   * on success, and checkoutPaymentFailedFlow on failure.
   */
  trackCustomerPurchaseForCheckout(host: CheckoutComFlowHost, cpId: string): void {
    this.paymentService.trackCustomerPurchaseForCheckout(
      cpId,
      host.pathString,
      () => {
        if (!this.isHostAlive(host)) {
          return;
        }
        // Legacy payment.page tracking callback (not the main 3DS success path):
        // preserves prior handleCheckoutComSuccessPayment({}, ...) wiring.
        // Pass cpId so payment_success is not dropped by getCheckOutComResponse$ take(1).
        this.checkoutComService.handleCheckoutComSuccessPayment(
          {},
          host.purchaseDetail,
          host.altprosubDetails ?? {},
          (user) => {
            if (!this.isHostAlive(host)) {
              return;
            }
            host.proceedAfterCheckoutSuccess?.(user, {});
          },
          cpId
        );
      },
      () => {
        if (!this.isHostAlive(host)) {
          return;
        }
        host.checkoutPaymentFailedFlow();
      }
    );
  }

  /** Shared UI reset when Checkout.com config load fails or publicKey is missing. */
  private applyCheckoutConfigLoadFailure(host: CheckoutComFlowHost): void {
    if (!this.isHostAlive(host)) {
      return;
    }
    host.addingCard = false;
    host.addNewCardInProgress = false;
    host.showAddNewCard = true;
    host.showCheckOutComSpinner = false;
    host.errorMessage = "Failed to load payment form";
  }

  /**
   * Boots Checkout.com Flow on the payment host.
   * Preserves first-mount / remount / post-3DS remount entry (callers reset
   * checkoutFlowRedirectHandled before remount where required).
   */
  loadCheckoutComFlow(host: CheckoutComFlowHost, data: Data): void {
    if (!this.isHostAlive(host)) {
      return;
    }

    const sessionId = ++this.flowSessionId;
    host.checkoutFlowSessionId = sessionId;
    this.processedCheckoutResponseKeys.clear();
    this.paymentActionSubscriptions.unsubscribe();
    this.paymentActionSubscriptions = new Subscription();

    host.checkOutComData = data;
    host.generateNewUUID();
    const storedUuid = localStorage.getItem("myuuid");
    if (storedUuid && storedUuid !== "null") {
      host.pathString = storedUuid;
    } else if (!host.pathString || String(host.pathString) === "null") {
      host.pathString = uuidv4();
      localStorage.setItem("myuuid", host.pathString);
    }
    this.paymentService.setcheckOutComDocId({ docId: String(host.pathString) });
    host.isCheckoutCom = true;
    host.errorMessage = "";
    host.showCheckOutComSpinner = true;

    const queryParams = host.actRoute.snapshot.queryParams || {};
    this.configLoadSubscription?.unsubscribe();
    const config$ = this.paymentService.getCheckoutApiDetails(queryParams);
    if (!config$) {
      this.applyCheckoutConfigLoadFailure(host);
      return;
    }
    const isCurrentConfigSession = (): boolean =>
      this.isHostAlive(host) &&
      this.flowSessionId === sessionId &&
      host.checkoutFlowSessionId === sessionId;

    this.configLoadSubscription = config$
      .pipe(
        take(1),
        catchError((err) => {
          this.authService.errorHandlerLogs("Checkout.com config failed:", err);
          if (isCurrentConfigSession()) {
            this.applyCheckoutConfigLoadFailure(host);
          }
          return of(null);
        })
      )
      .subscribe((res: Data | null) => {
        if (!isCurrentConfigSession()) {
          return;
        }
        if (!res?.data?.publicKey) {
          this.applyCheckoutConfigLoadFailure(host);
          return;
        }

        host.keys = res.data;
        host.isCheckoutComform = true;
        host.initializeCheckoutFlowIFrames?.();
      });
  }

  /**
   * Runs card-tokenized payment continuation for a live checkout host.
   * Do not retain `host` after `releaseHost` / destroy — it is only held via
   * liveMountHandlers / subscriptions that `releaseHost` clears.
   */
  async onCardTokenized(host: CheckoutComFlowHost, event: Data): Promise<void> {
    if (!this.isHostAlive(host) || host.cardTokenizedCalled) return;
    // Offline check before latching cardTokenizedCalled so retries remain possible.
    if (!window.navigator.onLine) return;
    host.cardTokenizedCalled = true;
    host.checkoutSessionExpiredHandled = false;
    this.clearCheckoutIframeRecovery(host);
    host.GTMSegment.fourxCtaClicked();
    const purchaseDetail = { ...host.purchaseDetail, ...host.getCheckCardDetailId() };
    host.showCheckOutComSpinner = true;
    // Unmount Flow before hiding the host form so Angular does not destroy
    // #cko-flow-container while the Checkout.com SDK still holds it (HATDEV-99039 / 1N9X).
    this.unmount();
    host.isCheckoutComform = false;
    host.isCheckoutResponseReceived = false;
    host.fixFooter = false;
    host.token = event.token;
    // Per-attempt in-memory dedupe; durable cross-attempt guard remains show_toast / requestid.
    this.processedCheckoutResponseKeys.clear();
    const attemptFlowSessionId = this.flowSessionId;
    const attemptHostSessionId = host.checkoutFlowSessionId;
    const isSameCheckoutAttempt = (): boolean =>
      this.isHostAlive(host) &&
      this.flowSessionId === attemptFlowSessionId &&
      host.checkoutFlowSessionId === attemptHostSessionId;

    let sessionValid: boolean;
    try {
      sessionValid = await host.ensureCheckoutSessionValid();
    } catch (err) {
      if (!isSameCheckoutAttempt()) {
        return;
      }
      host.cardTokenizedCalled = false;
      host.showCheckOutComSpinner = false;
      host.isCheckoutComform = true;
      host.handleCheckoutSessionExpired(err as Data);
      return;
    }
    if (!isSameCheckoutAttempt()) {
      return;
    }
    if (sessionValid === false) {
      host.cardTokenizedCalled = false;
      host.showCheckOutComSpinner = false;
      host.isCheckoutComform = true;
      host.handleCheckoutSessionExpired();
      return;
    }

    const msn = host.checkOutComData?.msn || localStorage.getItem("msn");
    if (!msn) {
      host.cardTokenizedCalled = false;
      host.showCheckOutComSpinner = false;
      host.isCheckoutComform = true;
      host.checkoutPaymentFailedFlow();
      return;
    }
    const msnParts = this.normalizeCheckoutMsnParts(
      msn,
      host.checkOutComData?.country_code ?? localStorage.getItem("country_code")
    );

    const intPrice = host.purchaseDetail?.offers?.int_price?.[host.preferredCurrency];
    let preferredAmount: string | Data;
    if (intPrice != null && intPrice !== "") {
      const n = Number(intPrice);
      preferredAmount = Number.isFinite(n) ? n.toFixed(2) : String(intPrice);
    } else if (host.purchaseDetail?.prize && !isNaN(host.purchaseDetail?.prize)) {
      const n = Number(parseFloat(host.purchaseDetail?.prize));
      preferredAmount = Number.isFinite(n) ? n.toFixed(2) : host.checkOutComData?.prize;
    } else {
      preferredAmount = host.checkOutComData?.prize;
    }

    const auth = getAuth();
    const TP1 = localStorage.getItem("TP1") || "";
    const TP2 = localStorage.getItem("TP2") || "";
    const TP3 = localStorage.getItem("TP3") || "";
    const T_ID = localStorage.getItem("T_ID") || "";

    const paymentData = {
      token: host.token,
      amount:
        host.purchaseDetail?.addons && host.purchaseDetail.addons.length > 0
          ? host.addonTotalAmount
          : preferredAmount,
      currency: host.preferredCurrency,
      email: this.resolveCheckoutEmail(host, msn),
      name: host.checkOutComData?.name,
      phone: msn,
      userId: host.userId,
      logId: host.pathString,
      msn: {
        country_code: msnParts.country_code,
        number: msnParts.number,
      },
      offer_id:
        purchaseDetail && purchaseDetail.offers && purchaseDetail.offers.offerid
          ? purchaseDetail.offers.offerid
          : purchaseDetail?.offer_id,
      prosub_id: purchaseDetail?.prosub_id ? purchaseDetail.prosub_id : localStorage.getItem("prosub_id"),
      anonymous_id: auth?.currentUser?.isAnonymous ? auth.currentUser.uid : null,
      tp1: TP1 === "null" ? "" : TP1,
      tp2: TP2 === "null" ? "" : TP2,
      tp3: TP3 === "null" ? "" : TP3,
      t_id: T_ID === "null" ? "" : T_ID,
      Other: purchaseDetail?.other
        ? purchaseDetail.other
        : localStorage.getItem("Other")
          ? localStorage.getItem("Other")
          : host.getCookie("Other")
            ? host.getCookie("Other")
            : "",
      value: purchaseDetail?.offers?.price,
      preferred_currency: {
        Value: purchaseDetail?.offers?.int_price?.[host.preferredCurrency]
          ? purchaseDetail.offers?.int_price?.[host.preferredCurrency]
          : purchaseDetail?.prize,
        currency: host.preferredCurrency,
      },
      p_type: purchaseDetail?.p_type ? purchaseDetail?.p_type : "21",
      visitorid: 111,
      requestid: uuidv4(),
      add_on: (purchaseDetail?.addons || [])
        .map((data) => ({
          ...data,
          offer_id: String(data.offer_id), // Convert offer_id to string for consistent comparison
        }))
        .filter(
          (item, index, self) => index === self.findIndex((t) => t?.offer_id === item?.offer_id) // Keep the first occurrence of each offer_id
        ),
      product_description: Array.isArray(purchaseDetail?.offers?.description)
        ? purchaseDetail?.offers?.description?.[0]
        : purchaseDetail?.offers?.description,
      payment_reason: host.paymentReason,
    };

    const unsubscribeCheckoutResponse = (): void => {
      host.getCheckOutComResponseSubscription?.unsubscribe();
      host.getCheckOutComResponseSubscription = undefined;
    };

    const attemptRequestId = String(paymentData.requestid);

    // Subscribe before starting the cloud request so a fast emission is not missed.
    host.getCheckOutComResponseSubscription?.unsubscribe();
    host.getCheckOutComResponseSubscription = this.paymentService.getCheckOutComResponse$.subscribe(
      (paymentResData: Data) => {
        if (!isSameCheckoutAttempt()) {
          return;
        }

        // Ignore responses that correlate to a different checkout log/doc when present.
        const responseLogId =
          paymentResData?.logId ??
          paymentResData?.docId ??
          paymentResData?.payment_request?.log_id;
        if (responseLogId != null && String(responseLogId) !== String(host.pathString)) {
          return;
        }

        // Prefer requestid correlation when the backend echoes it on payment_request.
        const responseRequestId =
          paymentResData?.payment_request?.requestid ??
          paymentResData?.payment_request?.request_id ??
          paymentResData?.requestid;
        if (responseRequestId != null && String(responseRequestId) !== attemptRequestId) {
          return;
        }

        const paymentResponse = paymentResData?.payment_response;
        host.customerPurchaseEncryptedId =
          paymentResData?.payment_request?.customer_purchase?.encrypted_customer_purchase_id;

        if (host.customerPurchaseEncryptedId) {
          this.paymentService.salesJourneyCustomerPurchaseId$.next(host.customerPurchaseEncryptedId);
        }
        host.showCheckOutComSpinner = false;

        // Already-finalized response for this attempt (durable show_toast + matching requestid).
        if (
          paymentResponse?.payment_status &&
          paymentResponse?.show_toast &&
          responseRequestId != null &&
          String(responseRequestId) === attemptRequestId
        ) {
          unsubscribeCheckoutResponse();
          return;
        }

        // Durable per-attempt marker: show_toast survives loadCheckoutComFlow clearing
        // processedCheckoutResponseKeys and pathString reuse of localStorage.myuuid.
        const dedupeKey = `${host.pathString}|${attemptRequestId}|${paymentResponse?.payment_id ?? paymentResponse?.id ?? ""}|${paymentResponse?.payment_status}`;
        if (
          paymentResponse?.payment_status &&
          !paymentResponse?.show_toast &&
          !this.processedCheckoutResponseKeys.has(dedupeKey) &&
          !host.isCheckoutResponseReceived
        ) {
          this.processedCheckoutResponseKeys.add(dedupeKey);
          host.isCheckoutResponseReceived = true;
          host.cardTokenizedCalled = false;
          host.paymentHold = false;
          // In-memory guard for the shared response object (matches legacy payment hosts).
          paymentResponse.show_toast = true;

          if (paymentResponse?.payment_status === "success") {
            const successCpId =
              paymentResData?.payment_request?.customer_purchase?.customer_purchase_id ||
              paymentResData?.payment_request?.customer_purchase?.encrypted_customer_purchase_id ||
              host.customerPurchaseEncryptedId;
            this.paymentService.emitSyndicatePurchaseComplete();
            host.loadingService.dismiss();
            // Prefer plain id when both exist — already ordered above.
            // Pass payment_request so GTM can use the amount/offer_id written for this charge
            // (host price/offerid shapes often fail the payment_success guard on new-card).
            this.checkoutComService.handleCheckoutComSuccessPayment(
              paymentResponse,
              purchaseDetail,
              host.altprosubDetails ?? {},
              (user) => {
                if (!isSameCheckoutAttempt()) {
                  return;
                }
                host.proceedAfterCheckoutSuccess?.(user, paymentResponse);
              },
              successCpId,
              paymentResData?.payment_request
            );
            if (host.isFourCCheckoutRoute()) {
              void host.showFourCAlertFromMappedCheckoutStatus(paymentResponse);
            } else {
              const docId = this.paymentService.resolveFourCPaymentFeedbackDocId(paymentResponse);
              const fallback = this.paymentService.mapFourCPaymentFeedbackFallback(docId);

              this.authService.getConstantsMsg(docId).then((translatedMsg) => {
                if (!isSameCheckoutAttempt()) {
                  return;
                }
                const defaultMsg = {
                  message: {
                    line_1: fallback.message.line_1,
                    line_2: fallback.message.line_2 ?? "",
                  },
                  style: {
                    icon: fallback.style.icon,
                    type:
                      fallback.style.type === "success"
                        ? "Success"
                        : fallback.style.type === "error"
                          ? "Error"
                          : fallback.style.type === "info"
                            ? "Info"
                            : fallback.style.type === "warning"
                              ? "Warning"
                              : "Error",
                    duration_ms: 5000,
                  },
                };
                this.authService.showToastMessage(translatedMsg, defaultMsg);
              }).catch((error) => {
                if (!isSameCheckoutAttempt()) {
                  return;
                }
                this.authService.errorHandlerLogs("Checkout success feedback message:", error);
                this.authService.showToastMessage(null, {
                  message: {
                    line_1: fallback.message.line_1,
                    line_2: fallback.message.line_2 ?? "",
                  },
                  style: {
                    icon: fallback.style.icon,
                    type: fallback.style.type === "success" ? "Success" : "Error",
                    duration_ms: 5000,
                  },
                });
              });
            }

            // Clone for Firestore so shared paymentResponse is not mutated; show_toast kept for legacy.
            const paymentResponseForStore = { ...paymentResponse, show_toast: true };

            const path_id = `JL4LGZDCJv0wpAfFrAPc`;
            const doc_data = { payment_response: paymentResponseForStore };
            const dv = { docId: host.pathString };
            const method = "update";

            this.trackPaymentAction(
              this.authService
                .getV2CloudFunction("updatefirestoredoc", {
                  path_id,
                  doc_data,
                  dv,
                  method,
                })
                .subscribe({
                  error: (error) => {
                    this.authService.errorHandlerLogs("Checkout Flow updatefirestoredoc failed:", error);
                  },
                })
            );

            const ispostNameMatched = localStorage.getItem("ispostNameMatched");
            const delayTime = ispostNameMatched ? 20000 : 0;

            const waitForStoredCards = new Promise<void>((resolve) => {
              let settled = false;
              const finish = (): void => {
                if (settled) {
                  return;
                }
                settled = true;
                try {
                  if (this.isHostAlive(host)) {
                    host.loadingService.dismiss();
                  }
                } catch {
                  // Best-effort loader cleanup during teardown.
                }
                resolve();
              };

              if (!isSameCheckoutAttempt()) {
                finish();
                return;
              }
              host.loadingService.present();
              const storedCardsDelayTimer = setTimeout(() => {
                if (!isSameCheckoutAttempt()) {
                  finish();
                  return;
                }
                host.loadingService.dismiss();

                // Modular Firestore: host.firestore must be the modular Firestore instance.
                const storedCardsCollectionRef = collection(
                  host.firestore,
                  `users/${host.userId}/stored_card`
                );

                let unsubscribeListener: (() => void) | null = null;
                const listenerTimeout = setTimeout(() => {
                  unsubscribeListener?.();
                  finish();
                }, 15000);

                unsubscribeListener = onSnapshot(
                  storedCardsCollectionRef,
                  {
                    next: (snapshot) => {
                      if (!isSameCheckoutAttempt()) {
                        clearTimeout(listenerTimeout);
                        unsubscribeListener?.();
                        finish();
                        return;
                      }
                      const documents = snapshot.docs.map((doc) => doc.data());
                      if (documents.length > 0) {
                        clearTimeout(listenerTimeout);
                        unsubscribeListener?.();
                        finish();
                      }
                    },
                    error: () => {
                      clearTimeout(listenerTimeout);
                      unsubscribeListener?.();
                      finish();
                    }
                  }
                );

                // Track listener + timeout so releaseHost cancels them with the session.
                this.trackPaymentAction(
                  new Subscription(() => {
                    clearTimeout(listenerTimeout);
                    unsubscribeListener?.();
                    finish();
                  })
                );
              }, delayTime);
              this.trackPaymentAction(
                new Subscription(() => {
                  clearTimeout(storedCardsDelayTimer);
                  finish();
                })
              );
            });

            waitForStoredCards.catch(() => {
              // finish() already dismisses the loader; keep catch to avoid unhandled rejection.
            });

            unsubscribeCheckoutResponse();
          } else {
            host.checkoutPaymentFailedFlow();
            host.cardTokenizedCalled = false;
            unsubscribeCheckoutResponse();
          }
        }

        if (host.isCheckoutResponseReceived) {
          unsubscribeCheckoutResponse();
        }
      }
    );
    if (host.getCheckOutComResponseSubscription) {
      this.trackPaymentAction(host.getCheckOutComResponseSubscription);
    }

    const generateCheckOutToken = host.paymentMode === "sandbox" ? "generatecheckouttokensbv2" : environment.cloudFunctions.generateCheckOutToken;
    this.trackPaymentAction(
      this.authService.getV2CloudFunction(generateCheckOutToken, paymentData).subscribe(
        (res) => {
          if (!isSameCheckoutAttempt()) {
            return;
          }
          if (!res) {
            host.cardTokenizedCalled = false;
            host.showCheckOutComSpinner = false;
            host.isCheckoutComform = true;
            host.checkoutPaymentFailedFlow();
            unsubscribeCheckoutResponse();
            return;
          }
          if (typeof res === "object" && Object.keys(res).length === 0) {
            host.cardTokenizedCalled = false;
            host.showCheckOutComSpinner = false;
            host.isCheckoutComform = true;
            host.handleCheckoutSessionExpired();
            unsubscribeCheckoutResponse();
            return;
          }
          if (!res.requiresRedirect && res.approved) {
            host.loadingService.present();
          } else {
            const responseCode = res?.response_code;
            const otpUrl = res?.redirectLink;
            // To check if details entered belongs to Non-3D secure enabled card, which is not allowed
            // To check if the redirect link is valid, which is required for further flow
            if (responseCode === "20150" || responseCode === "40101" || !otpUrl) {
              host.checkoutPaymentFailedFlow();
              host.cardTokenizedCalled = false;
              host.showCheckOutComSpinner = false;
              unsubscribeCheckoutResponse();
              return;
            }
            const trustedRedirect = this.trustCheckoutRedirectUrl(host, otpUrl);
            if (!trustedRedirect) {
              host.checkoutPaymentFailedFlow();
              host.cardTokenizedCalled = false;
              host.showCheckOutComSpinner = false;
              unsubscribeCheckoutResponse();
              return;
            }
            const encryptedCustomerPurchaseId = res?.encryptedCustomerPurchaseId;
            host.redirectlink = trustedRedirect;
            if (encryptedCustomerPurchaseId) {
              this.trackCustomerPurchaseForCheckout(host, encryptedCustomerPurchaseId);
            }
          }
        },
        (err) => {
          if (!isSameCheckoutAttempt()) {
            return err;
          }
          host.handleCheckoutSessionExpired(err);
          unsubscribeCheckoutResponse();
          return err;
        }
      )
    );
  }

}
